import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Play, Radio, Calendar, Send, BarChart3, Lock, Heart, Flame, Hand, Crown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataSaver } from "@/context/DataSaverContext";

const STAGE = "https://images.unsplash.com/photo-1733322991133-2de13aa12c57?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

const REACTIONS = [
  { key: "heart", Icon: Heart, color: "text-rose-400" },
  { key: "fire", Icon: Flame, color: "text-orange-400" },
  { key: "clap", Icon: Hand, color: "text-amber-300" },
  { key: "crown", Icon: Crown, color: "text-gold" },
];

export default function Watch() {
  const { dataSaver } = useDataSaver();
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [events, setEvents] = useState([]);
  const [live, setLive] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [floats, setFloats] = useState([]);
  const [overlay, setOverlay] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const chatEndRef = useRef(null);
  const prevReactions = useRef({});
  const celebratedMilestone = useRef(0);

  const celebrate = (milestone) => {
    const heavy = milestone >= 1000;
    const end = Date.now() + (heavy ? 2600 : 1200);
    const colors = ["#FF4D94", "#FF1A75", "#FFFFFF"];
    (function frame() {
      confetti({ particleCount: heavy ? 7 : 4, angle: 60, spread: heavy ? 90 : 60, origin: { x: 0 }, colors });
      confetti({ particleCount: heavy ? 7 : 4, angle: 120, spread: heavy ? 90 : 60, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    if (milestone >= 10000) setOverlay({ type: "sponsor", milestone });
    else if (milestone >= 1000) setOverlay({ type: "crownrain", milestone });
    else toast.success(`🎉 ${milestone.toLocaleString()} reactions!`, { description: "The crowd is on fire — keep cheering!" });
    if (milestone >= 1000) setTimeout(() => setOverlay(null), 6000);
  };

  const spawnFloat = (key) => {
    const id = Math.random().toString(36).slice(2);
    const left = 8 + Math.random() * 78;
    setFloats((f) => [...f, { id, key, left }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 2600);
  };

  const loadLive = () => api.get("/livestream").then((r) => {
    setLive(r.data);
    const rx = r.data.reactions || {};
    const prev = prevReactions.current;
    if (Object.keys(prev).length) {
      REACTIONS.forEach(({ key }) => {
        const delta = Math.min((rx[key] || 0) - (prev[key] || 0), 4);
        for (let i = 0; i < delta; i++) setTimeout(() => spawnFloat(key), i * 180);
      });
    }
    prevReactions.current = rx;
    const ms = rx.milestone || 0;
    if (ms > celebratedMilestone.current) {
      if (celebratedMilestone.current !== 0) celebrate(ms);
      celebratedMilestone.current = ms;
    }
  }).catch(() => {});

  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/sponsors").then((r) => setSponsors(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
    loadLive();
    const t = setInterval(loadLive, 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [live?.chat?.length]);

  const sendChat = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try { await api.post("/livestream/chat", { message: msg.trim() }); setMsg(""); loadLive(); }
    catch { /* ignore */ }
    finally { setSending(false); }
  };

  const votePoll = async (option) => {
    try { const { data } = await api.post("/livestream/poll/vote", { option }); setLive((s) => ({ ...s, poll: data })); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not vote"); }
  };

  const react = async (key) => {
    if (!user) return;
    spawnFloat(key);
    setLive((s) => ({ ...s, reactions: { ...(s?.reactions || {}), [key]: ((s?.reactions?.[key] || 0) + 1) } }));
    try {
      const { data } = await api.post("/livestream/reaction", { type: key });
      prevReactions.current = data;
      const ms = data.milestone || 0;
      if (ms > celebratedMilestone.current) { if (celebratedMilestone.current !== 0) celebrate(ms); celebratedMilestone.current = ms; }
    } catch { /* ignore */ }
  };

  const isLive = live?.live;
  const poll = live?.poll;
  const pollVotes = poll?.votes || {};
  const pollTotal = Object.values(pollVotes).reduce((a, b) => a + (b || 0), 0);
  const voted = poll?.voted;

  return (
    <div className="section py-12 sm:py-16" data-testid="watch-page">
      {overlay && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden bg-obsidian/60 backdrop-blur-sm" data-testid="milestone-overlay" onClick={() => setOverlay(null)}>
          {Array.from({ length: 26 }).map((_, i) => (
            <span key={i} className="crown-drop" style={{ left: `${(i * 3.8) % 100}%`, animationDuration: `${2.5 + (i % 5) * 0.5}s`, animationDelay: `${(i % 8) * 0.25}s` }}>
              <Crown className="h-6 w-6 text-gold" fill="currentColor" />
            </span>
          ))}
          <div className="relative z-10 mx-6 max-w-lg rounded-3xl border border-gold/40 bg-obsidian-600/90 p-8 text-center shadow-2xl">
            <Crown className="mx-auto h-12 w-12 text-gold" fill="currentColor" />
            <p className="mt-4 font-serif text-4xl text-amber-50">{overlay.milestone.toLocaleString()} Reactions!</p>
            {overlay.type === "sponsor" ? (
              <>
                <p className="mt-3 text-amber-100/70">This continental moment is brought to you by</p>
                <p className="mt-2 font-display text-2xl gold-text">{sponsors[0]?.name || "our proud sponsors"}</p>
                {sponsors[0]?.tier && <p className="text-xs uppercase tracking-widest text-gold/60">{sponsors[0].tier.replace(/\s*partner\s*/i, "")} Partner</p>}
              </>
            ) : (
              <p className="mt-3 text-amber-100/70">The crowd is roaring — the crown feels the love! 👑</p>
            )}
          </div>
        </div>
      )}
      <p className="kicker mb-2 flex items-center gap-2">
        <Radio className={`h-4 w-4 ${isLive ? "animate-pulse text-red-400" : "text-gold/60"}`} /> Livestream Center
      </p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50">Watch</h1>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        {/* Player */}
        <div className="lg:col-span-2">
          <div className="card-lux relative overflow-hidden">
            <div className="relative aspect-video bg-obsidian-600">
              {isLive && live?.url ? (
                <iframe title="livestream" src={live.url} className="h-full w-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="livestream-iframe" />
              ) : (
                <>
                  {!dataSaver && <img src={STAGE} alt="Continental stage" className="h-full w-full object-cover opacity-60" />}
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-obsidian/40">
                    <span className={`rounded-full border px-3 py-1 font-mono text-xs uppercase tracking-widest ${isLive ? "border-red-400/50 bg-red-500/10 text-red-300" : "border-gold/40 bg-gold/5 text-gold/80"}`}>
                      {isLive ? "● Live Now" : "Livestream · Offline"}
                    </span>
                    <button className="flex h-16 w-16 items-center justify-center rounded-full gold-bg text-obsidian" data-testid="livestream-play"><Play className="h-7 w-7" /></button>
                    <p className="font-serif text-xl text-amber-50 text-center px-6">{live?.title || "Grand Coronation Finale · Coming Soon"}</p>
                  </div>
                </>
              )}
              {isLive && (
                <span className="absolute left-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-red-500/90 px-3 py-1 font-mono text-[0.65rem] font-bold uppercase tracking-widest text-white" data-testid="live-badge">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live
                </span>
              )}
              {/* Floating reactions overlay */}
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                {floats.map((f) => {
                  const R = REACTIONS.find((r) => r.key === f.key);
                  return <span key={f.id} className="float-reaction" style={{ left: `${f.left}%` }}><R.Icon className={`h-7 w-7 ${R.color} drop-shadow-lg`} fill="currentColor" /></span>;
                })}
              </div>
            </div>
            {/* Reaction bar */}
            <div className="flex items-center justify-between gap-2 border-t border-gold/15 p-3" data-testid="reaction-bar">
              <div className="flex gap-2">
                {REACTIONS.map(({ key, Icon, color }) => (
                  <button key={key} onClick={() => react(key)} disabled={!user} data-testid={`reaction-${key}`}
                    className="group flex items-center gap-1.5 rounded-full border border-gold/25 bg-obsidian px-3 py-1.5 text-xs text-amber-100/80 transition-all hover:border-gold hover:scale-105 disabled:opacity-40 disabled:hover:scale-100">
                    <Icon className={`h-4 w-4 ${color} transition-transform group-active:scale-125`} />
                    <span className="font-mono tabular-nums">{(live?.reactions?.[key] || 0).toLocaleString()}</span>
                  </button>
                ))}
              </div>
              {!user && <span className="text-[0.65rem] text-muted-foreground"><Link to="/login" className="text-gold hover:underline">Sign in</Link> to react</span>}
            </div>
            {live?.title && isLive && <div className="p-4 pt-0"><p className="font-serif text-lg text-amber-50">{live.title}</p></div>}
          </div>

          {/* Live Poll */}
          {poll?.question && (
            <div className="card-lux mt-6 p-6" data-testid="live-poll">
              <p className="kicker mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Live Poll</p>
              <p className="font-serif text-xl text-amber-50">{poll.question}</p>
              <div className="mt-5 space-y-3">
                {(poll.options || []).map((opt) => {
                  const v = pollVotes[opt] || 0;
                  const pct = pollTotal ? Math.round((v / pollTotal) * 100) : 0;
                  return (
                    <button key={opt} onClick={() => votePoll(opt)} disabled={!user || voted} data-testid={`poll-option-${opt}`}
                      className="group relative block w-full overflow-hidden rounded-lg border border-gold/25 bg-obsidian px-4 py-3 text-left transition-colors hover:border-gold disabled:cursor-not-allowed">
                      <span className="absolute inset-y-0 left-0 bg-gold/15 transition-all" style={{ width: `${pct}%` }} />
                      <span className="relative flex items-center justify-between text-sm text-amber-50">
                        <span>{opt}</span><span className="font-mono text-gold">{pct}% · {v}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {voted && <p className="mt-3 text-xs text-emerald-accent" data-testid="poll-voted">✓ Thanks — your vote is counted</p>}
              {!user && <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3 w-3" /> <Link to="/login" className="text-gold hover:underline">Sign in</Link> to vote in the live poll</p>}
            </div>
          )}
        </div>

        {/* Live Chat */}
        <div className="card-lux flex h-[520px] flex-col" data-testid="live-chat">
          <div className="border-b border-gold/15 p-4"><p className="kicker">Live Chat</p></div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {(live?.chat || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Be the first to cheer on your delegation.</p>
            ) : (
              (live?.chat || []).map((c) => (
                <div key={c.id} className="text-sm">
                  <span className="font-semibold text-gold">{c.user}</span>
                  <span className="ml-2 text-amber-100/80">{c.message}</span>
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>
          {user ? (
            <form onSubmit={sendChat} className="flex gap-2 border-t border-gold/15 p-3">
              <input value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={280} placeholder="Say something…" data-testid="chat-input"
                className="flex-1 rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
              <button disabled={sending} data-testid="chat-send" className="flex items-center justify-center rounded-lg gold-bg px-3 text-obsidian disabled:opacity-50"><Send className="h-4 w-4" /></button>
            </form>
          ) : (
            <div className="border-t border-gold/15 p-3 text-center text-xs text-muted-foreground"><Link to="/login" className="text-gold hover:underline">Sign in</Link> to join the chat</div>
          )}
        </div>
      </div>

      <div className="mt-12 grid gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <p className="kicker mb-6">On Demand</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {videos.map((v) => (
              <div key={v.id} className="card-lux group">
                <div className="relative aspect-video overflow-hidden bg-obsidian-600">
                  {!dataSaver && <img src={v.thumbnail} alt={v.title} loading="lazy" className="h-full w-full object-cover opacity-70 group-hover:scale-105 transition-transform" />}
                  <span className="absolute inset-0 flex items-center justify-center"><span className="flex h-12 w-12 items-center justify-center rounded-full gold-bg text-obsidian"><Play className="h-5 w-5" /></span></span>
                  <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[0.6rem] text-amber-200">{v.duration}</span>
                </div>
                <div className="p-4"><p className="kicker mb-1 text-[0.6rem]">{v.type}</p><p className="text-sm text-amber-50">{v.title}</p></div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="kicker mb-6">Program Schedule</p>
          <div className="card-lux divide-y divide-gold/10">
            {events.map((e) => (
              <div key={e.id} className="flex items-start gap-3 p-4">
                <Calendar className="mt-0.5 h-4 w-4 text-gold" />
                <div>
                  <p className="text-sm text-amber-50">{e.name}</p>
                  <p className="text-xs text-muted-foreground">{new Date(e.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · {e.venue}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
